import type { DateRange } from "@daypicker/react";
import type { ColumnDef, Table } from "@tanstack/react-table";
import { Check, ChevronLeft, ChevronRight, ListFilter, Search, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { DateRangeCalendar } from "@/components/ui/DateRangePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  TOOLBAR_FILTER_LABEL_HIDE_MAX_WIDTH,
  toolbarFilterLabelClass,
} from "@/lib/toolbar-compact";
import { cn } from "@/lib/utils";

/**
 * True when the surrounding toolbar has collapsed its filter labels to
 * icon-only (see {@link toolbarFilterLabelClass}). Lets icon-only toolbar
 * buttons restore a hover tooltip for their otherwise-missing label, while
 * staying tooltip-free when the label text is visible. Defaults to false so
 * buttons rendered outside a {@link ToolbarCompactProvider} never show it.
 */
const ToolbarCompactContext = React.createContext(false);

export function useToolbarCompact(): boolean {
  return React.useContext(ToolbarCompactContext);
}

/**
 * Renders the toolbar container element and measures its width, exposing a
 * compact flag (label-collapsed state) to descendant filter buttons. The
 * threshold mirrors {@link toolbarFilterLabelClass} so the tooltip appears
 * exactly when the label is hidden.
 */
export const ToolbarCompactProvider = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, ...props }, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [isCompact, setIsCompact] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const update = () =>
      setIsCompact(
        el.getBoundingClientRect().width <= TOOLBAR_FILTER_LABEL_HIDE_MAX_WIDTH,
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const setRefs = (node: HTMLDivElement | null) => {
    innerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  return (
    <div ref={setRefs} {...props}>
      <ToolbarCompactContext.Provider value={isCompact}>
        {children}
      </ToolbarCompactContext.Provider>
    </div>
  );
});
ToolbarCompactProvider.displayName = "ToolbarCompactProvider";

export type FilterFieldOption = {
  label: string;
  value: string;
  /** Optional sub-section heading. Options sharing a group render together under
   *  a clickable heading that toggles the whole group (select-all / clear). */
  group?: string;
};

export type FilterField =
  | { id: string; label: string; type: "multi"; options?: FilterFieldOption[] }
  | { id: string; label: string; type: "date" }
  | { id: string; label: string; type: "search"; placeholder?: string }
  /**
   * Single-select field controlled externally (not backed by a TanStack column
   * filter) — e.g. a server-side scope toggle. It's "active" when `value`
   * differs from `defaultValue`.
   */
  | {
      id: string;
      label: string;
      type: "choice";
      options: FilterFieldOption[];
      value: string;
      defaultValue: string;
      onChange: (value: string) => void;
    }
  /**
   * Fully custom field (not backed by a TanStack column). The consumer supplies
   * its own editor via `render`, plus the active state / chip summary and a
   * clear handler. Used for filters whose UI doesn't fit the built-in editors
   * (e.g. a grouped, color-coded calendar picker).
   */
  | {
      id: string;
      label: string;
      type: "custom";
      /** Whether this field is currently narrowing results. */
      active: boolean;
      /** Contribution to the Filters badge count (defaults to `active ? 1 : 0`). */
      count?: number;
      /** Short text shown in the active-filter chip. */
      summary?: string;
      /** Editor rendered when the field is drilled into / its chip is opened. */
      render: () => React.ReactNode;
      /** Reset this field to its inactive/default state. */
      onClear: () => void;
    };

/**
 * Declarative filter descriptor attached to a column via `meta.filter`, so a
 * column owns the definition of its own toolbar filter (single source of truth).
 * `deriveFilterFields` turns these into `FilterField`s (the column id becomes the
 * field id). `choice`-type fields are page-specific (externally controlled) and
 * are added as explicit extras rather than derived from columns.
 */
export type ColumnFilterMeta =
  | { type: "multi"; label: string; options?: FilterFieldOption[]; order?: number }
  | { type: "date"; label: string; order?: number }
  | { type: "search"; label: string; placeholder?: string; order?: number };

/** Per-field overrides supplied at composition time (e.g. runtime-derived options). */
export type FilterFieldOverride = {
  label?: string;
  options?: FilterFieldOption[];
  placeholder?: string;
};

/**
 * Builds `FilterField[]` from columns that declare `meta.filter`. Pages call this
 * in a `useMemo`, then compose the result with any page-specific extras (e.g. a
 * Deep Analysis `choice` field) and, if desired, filter it per breakpoint.
 *
 * @param overrides keyed by column/field id — merged onto the derived field, used
 *   for runtime data such as option lists that can't live statically on a column.
 */
export function deriveFilterFields(
  columns: ColumnDef<any, any>[],
  overrides: Record<string, FilterFieldOverride> = {},
): FilterField[] {
  const entries: { field: FilterField; order: number }[] = [];

  columns.forEach((column, index) => {
    const meta = column.meta?.filter;
    if (!meta) return;

    const id =
      column.id ??
      ("accessorKey" in column && column.accessorKey != null
        ? String(column.accessorKey)
        : undefined);
    if (!id) return;

    const override = overrides[id] ?? {};
    const label = override.label ?? meta.label;

    let field: FilterField;
    if (meta.type === "date") {
      field = { id, label, type: "date" };
    } else if (meta.type === "search") {
      field = { id, label, type: "search", placeholder: override.placeholder ?? meta.placeholder };
    } else {
      field = { id, label, type: "multi", options: override.options ?? meta.options };
    }

    entries.push({ field, order: meta.order ?? index });
  });

  return entries.sort((a, b) => a.order - b.order).map((entry) => entry.field);
}

/** One-tap preset. Either sets a column filter to fixed values, applies a date
 *  window to a date column, or toggles some externally-controlled state (e.g. a
 *  server-side scope). */
export type QuickFilterPreset =
  | {
      label: string;
      columnId: string;
      values: string[];
      /** Badge color classes (e.g. "bg-blue-50 text-blue-800") to match inline tags. */
      className?: string;
      /** "tag" (default) renders a colored pill; "button" renders an outline/filled toggle button. */
      variant?: "tag" | "button";
    }
  | {
      label: string;
      /** Date-type column id to set to a window around today when active. */
      columnId: string;
      /** Half-window size in days: covers `[today - dateWindowDays, today + dateWindowDays]`. */
      dateWindowDays: number;
      /** Badge color classes (e.g. "bg-blue-50 text-blue-800") to match inline tags. */
      className?: string;
      /** "tag" (default) renders a colored pill; "button" renders an outline/filled toggle button. */
      variant?: "tag" | "button";
    }
  | {
      label: string;
      /** Date-type column id to set to an open-ended range relative to today. */
      columnId: string;
      /** "past" covers everything before today; "future" covers today onward. */
      dateDirection: "past" | "future";
      /** Badge color classes (e.g. "bg-blue-50 text-blue-800") to match inline tags. */
      className?: string;
      /** "tag" (default) renders a colored pill; "button" renders an outline/filled toggle button. */
      variant?: "tag" | "button";
    }
  | {
      label: string;
      /** Date-type column id whose window this bound extends. */
      columnId: string;
      /**
       * Extends the active date window on one side of today: "past" pushes the
       * start back N days, "future" pushes the end forward N days. Independent
       * bounds on the same column combine into a single `[start, end]` window, so
       * a "-30 days" and a "+60 days" bound together yield `[today-30, today+60]`.
       */
      dateBound: { side: "past" | "future"; days: number };
      /** Badge color classes (e.g. "bg-blue-50 text-blue-800") to match inline tags. */
      className?: string;
      /** "tag" (default) renders a colored pill; "button" renders an outline/filled toggle button. */
      variant?: "tag" | "button";
    }
  | {
      label: string;
      /** Badge color classes (e.g. "bg-blue-50 text-blue-800") to match inline tags. */
      className?: string;
      /** "tag" (default) renders a colored pill; "button" renders an outline/filled toggle button. */
      variant?: "tag" | "button";
      /** Controlled active state (not backed by a column filter). */
      active: boolean;
      onToggle: (active: boolean) => void;
      /** Filter-field id this preset represents, so that field's chip is hidden while the preset is active. */
      fieldId?: string;
    };

// ============================================================================
// Shared value helpers - read/write directly against the TanStack column state
// so the menu and the (remaining) column-header filters stay in sync.
// ============================================================================

function getMultiValue(table: Table<any>, id: string): string[] {
  const value = table.getColumn(id)?.getFilterValue();
  return Array.isArray(value) ? (value as string[]) : [];
}

/** Options for a multi field: explicit list, or derived from the column's facets. */
function getMultiOptions(
  table: Table<any>,
  field: Extract<FilterField, { type: "multi" }>,
): FilterFieldOption[] {
  // Distinguish "no options provided" (derive from facets) from an explicitly
  // empty list (the caller means: show no options).
  if (field.options !== undefined) return field.options;
  const facets = table.getColumn(field.id)?.getFacetedUniqueValues();
  if (!facets) return [];
  const values = new Set<string>();
  for (const key of facets.keys()) {
    if (Array.isArray(key)) {
      key.forEach((k) => k != null && k !== "" && values.add(String(k)));
    } else if (key != null && key !== "") {
      values.add(String(key));
    }
  }
  return Array.from(values)
    .sort()
    .map((v) => ({ label: v, value: v }));
}

/** The single free-text search value stored as string[] on the column. */
function getSearchValue(table: Table<any>, id: string): string {
  const value = table.getColumn(id)?.getFilterValue();
  if (Array.isArray(value)) return (value as string[]).find((v) => v?.trim()) ?? "";
  return typeof value === "string" ? value : "";
}

function getDateValue(table: Table<any>, id: string): DateRange | undefined {
  const value = table.getColumn(id)?.getFilterValue() as
    | { startDate?: Date; endDate?: Date }
    | undefined;
  if (!value || (!value.startDate && !value.endDate)) return undefined;
  return { from: value.startDate, to: value.endDate };
}

function isFieldActive(table: Table<any>, field: FilterField): boolean {
  if (field.type === "date") return Boolean(getDateValue(table, field.id));
  if (field.type === "search") return getSearchValue(table, field.id).length > 0;
  if (field.type === "choice") return field.value !== field.defaultValue;
  if (field.type === "custom") return field.active;
  return getMultiValue(table, field.id).length > 0;
}

function fieldCount(table: Table<any>, field: FilterField): number {
  if (field.type === "date") return getDateValue(table, field.id) ? 1 : 0;
  if (field.type === "search") return getSearchValue(table, field.id) ? 1 : 0;
  if (field.type === "choice") return field.value !== field.defaultValue ? 1 : 0;
  if (field.type === "custom") return field.count ?? (field.active ? 1 : 0);
  return getMultiValue(table, field.id).length;
}

function summarizeField(table: Table<any>, field: FilterField): string {
  if (field.type === "date") {
    const value = getDateValue(table, field.id);
    if (!value?.from) return "";
    const fmt = (d?: Date) => (d ? d.toLocaleDateString() : "");
    if (value.to && value.from.getTime() !== value.to.getTime()) {
      return `${fmt(value.from)} – ${fmt(value.to)}`;
    }
    return fmt(value.from);
  }
  if (field.type === "search") {
    const value = getSearchValue(table, field.id);
    return value ? `"${value}"` : "";
  }
  if (field.type === "choice") {
    if (field.value === field.defaultValue) return "";
    return field.options.find((o) => o.value === field.value)?.label ?? field.value;
  }
  if (field.type === "custom") return field.summary ?? "";
  const options = getMultiOptions(table, field);
  const values = getMultiValue(table, field.id);
  const labels = values.map(
    (val) => options.find((o) => o.value === val)?.label ?? val,
  );
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

function totalActiveCount(table: Table<any>, fields: FilterField[]): number {
  return fields.reduce((sum, field) => sum + fieldCount(table, field), 0);
}

/** Corner count that sits on the button without changing its width. */
export function FilterCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="pointer-events-none absolute -right-1.5 -top-1.5 z-10 inline-flex h-4 min-w-4 items-center justify-center rounded-full border border-border bg-background px-1 text-[10px] font-medium leading-none text-slate-600 tabular-nums shadow-sm">
      {count}
    </span>
  );
}

function clearAll(table: Table<any>, fields: FilterField[]) {
  fields.forEach((field) => {
    if (field.type === "choice") {
      if (field.value !== field.defaultValue) field.onChange(field.defaultValue);
    } else if (field.type === "custom") {
      if (field.active) field.onClear();
    } else {
      table.getColumn(field.id)?.setFilterValue(undefined);
    }
  });
}

function arraysEqualUnordered(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

// ============================================================================
// Field editors
// ============================================================================

/** Groups options by their `group`, preserving first-seen order. Returns a
 *  single unnamed group when no option declares one (the flat, ungrouped case). */
function groupOptions(
  options: FilterFieldOption[],
): { name?: string; options: FilterFieldOption[] }[] {
  if (!options.some((o) => o.group)) return [{ options }];
  const groups: { name?: string; options: FilterFieldOption[] }[] = [];
  const byName = new Map<string, FilterFieldOption[]>();
  for (const option of options) {
    const name = option.group ?? "";
    let bucket = byName.get(name);
    if (!bucket) {
      bucket = [];
      byName.set(name, bucket);
      groups.push({ name: name || undefined, options: bucket });
    }
    bucket.push(option);
  }
  return groups;
}

function MultiSelectList({
  table,
  field,
}: {
  table: Table<any>;
  field: Extract<FilterField, { type: "multi" }>;
}) {
  const column = table.getColumn(field.id);
  const selected = new Set(getMultiValue(table, field.id));
  const options = getMultiOptions(table, field);
  const groups = groupOptions(options);

  const commit = (next: Set<string>) => {
    const arr = Array.from(next);
    column?.setFilterValue(arr.length ? arr : undefined);
  };

  const renderOption = (option: FilterFieldOption, indent: boolean) => {
    const isSelected = selected.has(option.value);
    return (
      <CommandItem
        key={option.value}
        value={option.label}
        onSelect={() => {
          const next = new Set(selected);
          if (isSelected) next.delete(option.value);
          else next.add(option.value);
          commit(next);
        }}
        onPointerDown={(e) => e.preventDefault()}
        className={cn(indent && "pl-7")}
      >
        <div
          className={cn(
            "mr-2 flex h-4 w-4 items-center justify-center rounded-xs border border-primary",
            isSelected
              ? "bg-primary text-primary-foreground"
              : "opacity-50 [&_svg]:invisible",
          )}
        >
          <Check className="h-4 w-4" />
        </div>
        <span>{option.label}</span>
      </CommandItem>
    );
  };

  return (
    <Command>
      <CommandInput placeholder={field.label} />
      <CommandList className="max-h-64 custom-scrollbar">
        <CommandEmpty>No results found.</CommandEmpty>
        {groups.map((group, index) => {
          const selectedInGroup = group.options.filter((o) => selected.has(o.value)).length;
          const allSelected = group.options.length > 0 && selectedInGroup === group.options.length;
          const someSelected = selectedInGroup > 0 && !allSelected;
          return (
            <CommandGroup key={group.name ?? `group-${index}`}>
              {group.name && (
                // Clicking the sub-section heading toggles every option in it, so
                // e.g. "Key Dates" filters by all its options at once.
                <CommandItem
                  value={`select all ${group.name}`}
                  onSelect={() => {
                    const next = new Set(selected);
                    if (allSelected) group.options.forEach((o) => next.delete(o.value));
                    else group.options.forEach((o) => next.add(o.value));
                    commit(next);
                  }}
                  onPointerDown={(e) => e.preventDefault()}
                  className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-xs border border-primary",
                      allSelected
                        ? "bg-primary text-primary-foreground"
                        : someSelected
                          ? "bg-primary/30 text-primary-foreground [&_svg]:invisible"
                          : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className="h-4 w-4" />
                  </div>
                  <span>{group.name}</span>
                </CommandItem>
              )}
              {group.options.map((option) => renderOption(option, Boolean(group.name)))}
            </CommandGroup>
          );
        })}
        {selected.size > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => column?.setFilterValue(undefined)}
                onPointerDown={(e) => e.preventDefault()}
                className="justify-center text-center"
              >
                Clear
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}

function DateFilterCalendar({
  table,
  field,
}: {
  table: Table<any>;
  field: Extract<FilterField, { type: "date" }>;
}) {
  const column = table.getColumn(field.id);
  const value = getDateValue(table, field.id);

  return (
    <DateRangeCalendar
      value={value}
      onChange={(range) => {
        if (range) {
          column?.setFilterValue({ startDate: range.from, endDate: range.to });
        } else {
          column?.setFilterValue(undefined);
        }
      }}
      initialMonth={new Date()}
      footerPresets={["today", "next7Days", "next30Days", "next90Days"]}
      showClearSelection
      showRangeSummary
      showTopControls
      onClearSelection={() => column?.setFilterValue(undefined)}
    />
  );
}

function SearchFilterInput({
  table,
  field,
}: {
  table: Table<any>;
  field: Extract<FilterField, { type: "search" }>;
}) {
  const column = table.getColumn(field.id);
  const externalValue = getSearchValue(table, field.id);
  const [value, setValue] = React.useState(externalValue);
  // Latest typed value, so the unmount flush can commit it.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  // Last value we either wrote to the column or synced down from it, used to
  // tell our own debounced writes apart from external changes.
  const lastSyncedRef = React.useRef(externalValue);

  // Sync the input down when the column filter changes externally (e.g.
  // "Clear all" or removing the chip), without clobbering in-progress typing.
  React.useEffect(() => {
    if (externalValue !== lastSyncedRef.current) {
      lastSyncedRef.current = externalValue;
      setValue(externalValue);
    }
  }, [externalValue]);

  // Debounced write of the typed value into the column filter.
  React.useEffect(() => {
    const id = setTimeout(() => {
      lastSyncedRef.current = value.trim();
      column?.setFilterValue(value.trim() ? [value.trim()] : undefined);
    }, 250);
    return () => clearTimeout(id);
  }, [value, column]);

  // Radix unmounts the popover content on close; flush any value still within
  // the debounce window so fast type-then-close doesn't drop the last keystrokes.
  React.useEffect(() => {
    return () => {
      const pending = valueRef.current.trim();
      column?.setFilterValue(pending ? [pending] : undefined);
    };
  }, [column]);

  return (
    <div className="p-2">
      <div className="flex items-center gap-2 rounded-md border border-gray-200 px-2">
        <Search className="h-4 w-4 shrink-0 text-gray-400" />
        <input
          autoFocus
          type="text"
          value={value}
          placeholder={field.placeholder ?? "Word or phrase..."}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 w-full bg-transparent text-sm outline-hidden placeholder:text-gray-400"
        />
        {value && (
          <button
            className="text-gray-400 hover:text-gray-700"
            onClick={() => setValue("")}
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ChoiceList({
  field,
}: {
  field: Extract<FilterField, { type: "choice" }>;
}) {
  return (
    <Command>
      <CommandList>
        <CommandGroup className="max-h-64 overflow-auto custom-scrollbar">
          {field.options.map((option) => {
            const isSelected = field.value === option.value;
            return (
              <CommandItem
                key={option.value}
                onSelect={() => field.onChange(option.value)}
                onPointerDown={(e) => e.preventDefault()}
              >
                <div
                  className={cn(
                    "mr-2 flex h-4 w-4 items-center justify-center rounded-full border border-primary",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <Check className="h-3 w-3" />
                </div>
                <span>{option.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

function FieldEditorContent({
  table,
  field,
}: {
  table: Table<any>;
  field: FilterField;
}) {
  if (field.type === "date") return <DateFilterCalendar table={table} field={field} />;
  if (field.type === "search") return <SearchFilterInput table={table} field={field} />;
  if (field.type === "choice") return <ChoiceList field={field} />;
  if (field.type === "custom") return <>{field.render()}</>;
  return <MultiSelectList table={table} field={field} />;
}

// ============================================================================
// Active-filter chips (rendered next to the Filters button)
// ============================================================================

function FilterChip({
  table,
  field,
}: {
  table: Table<any>;
  field: FilterField;
}) {
  return (
    <div className="inline-flex h-8 items-center rounded-md bg-accent text-sm text-accent-foreground">
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex h-full items-center gap-1.5 rounded-l-md pl-2.5 pr-1.5 hover:bg-black/5">
            <span className="font-medium opacity-70">{field.label}</span>
            <span className="max-w-[160px] truncate">
              {summarizeField(table, field)}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={
            field.type === "date"
              ? "w-auto p-0"
              : field.type === "custom"
                ? "w-[260px] p-0"
                : "w-[240px] p-0"
          }
        >
          <FieldEditorContent table={table} field={field} />
        </PopoverContent>
      </Popover>
      <button
        className="flex h-full items-center rounded-r-md px-1.5 opacity-60 hover:opacity-100"
        onClick={() => {
          if (field.type === "choice") field.onChange(field.defaultValue);
          else if (field.type === "custom") field.onClear();
          else table.getColumn(field.id)?.setFilterValue(undefined);
        }}
        aria-label={`Remove ${field.label} filter`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ============================================================================
// Quick-filter preset tags (rendered inside the menu and inline in the bar)
// ============================================================================

/** Computes a `[today - days, today + days]` range for a date-window preset. */
export function dateWindowRange(days: number): { startDate: Date; endDate: Date } {
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  startDate.setDate(startDate.getDate() - days);
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  endDate.setDate(endDate.getDate() + days);
  return { startDate, endDate };
}

/** Computes an open-ended range relative to today for a date-direction preset.
 *  "past" covers everything before today; "future" covers today onward. */
export function dateDirectionRange(direction: "past" | "future"): { startDate: Date; endDate: Date } {
  if (direction === "future") {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate: new Date(2999, 11, 31, 23, 59, 59, 999) };
  }
  const endDate = new Date();
  endDate.setDate(endDate.getDate() - 1);
  endDate.setHours(23, 59, 59, 999);
  return { startDate: new Date(1900, 0, 1, 0, 0, 0, 0), endDate };
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Boundary date for a one-sided window: "past" is N days before today (start of
 *  day), "future" is N days after today (end of day). */
export function dateBoundDate(side: "past" | "future", days: number): Date {
  const d = new Date();
  if (side === "past") {
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - days);
  } else {
    d.setHours(23, 59, 59, 999);
    d.setDate(d.getDate() + days);
  }
  return d;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/** A date-bound preset is active when the column's window reaches its boundary. */
function isDateBoundActive(
  table: Table<any>,
  columnId: string,
  side: "past" | "future",
  days: number,
): boolean {
  const value = table.getColumn(columnId)?.getFilterValue() as
    | { startDate?: Date | string; endDate?: Date | string }
    | undefined;
  const target = dateBoundDate(side, days);
  if (side === "past") {
    return Boolean(value?.startDate) && isSameCalendarDay(new Date(value!.startDate!), target);
  }
  return Boolean(value?.endDate) && isSameCalendarDay(new Date(value!.endDate!), target);
}

function isDateRangeActive(
  table: Table<any>,
  columnId: string,
  range: { startDate: Date; endDate: Date },
): boolean {
  const value = table.getColumn(columnId)?.getFilterValue() as
    | { startDate?: Date | string; endDate?: Date | string }
    | undefined;
  if (!value?.startDate || !value?.endDate) return false;
  return (
    isSameCalendarDay(new Date(value.startDate), range.startDate) &&
    isSameCalendarDay(new Date(value.endDate), range.endDate)
  );
}

function isPresetActive(table: Table<any>, preset: QuickFilterPreset) {
  if ("onToggle" in preset) return preset.active;
  if ("dateWindowDays" in preset)
    return isDateRangeActive(table, preset.columnId, dateWindowRange(preset.dateWindowDays));
  if ("dateBound" in preset)
    return isDateBoundActive(table, preset.columnId, preset.dateBound.side, preset.dateBound.days);
  if ("dateDirection" in preset)
    return isDateRangeActive(table, preset.columnId, dateDirectionRange(preset.dateDirection));
  return arraysEqualUnordered(getMultiValue(table, preset.columnId), preset.values);
}

/** The filter-field id a preset represents (for hiding a redundant field chip). */
function presetFieldId(preset: QuickFilterPreset): string | undefined {
  if ("onToggle" in preset) return preset.fieldId;
  return preset.columnId;
}

export function QuickFilterTags({
  table,
  presets,
}: {
  table: Table<any>;
  presets: QuickFilterPreset[];
}) {
  const toggle = (preset: QuickFilterPreset, active: boolean) => {
    if ("onToggle" in preset) {
      preset.onToggle(!active);
      return;
    }
    const column = table.getColumn(preset.columnId);
    if ("dateWindowDays" in preset) {
      column?.setFilterValue(active ? undefined : dateWindowRange(preset.dateWindowDays));
      return;
    }
    if ("dateBound" in preset) {
      // Merge with the current window so past/future bounds toggle independently.
      const value = column?.getFilterValue() as
        | { startDate?: Date | string; endDate?: Date | string }
        | undefined;
      let start = value?.startDate ? new Date(value.startDate) : undefined;
      let end = value?.endDate ? new Date(value.endDate) : undefined;
      const { side, days } = preset.dateBound;
      if (side === "past") {
        start = active ? startOfToday() : dateBoundDate("past", days);
        if (!end) end = endOfToday();
      } else {
        end = active ? endOfToday() : dateBoundDate("future", days);
        if (!start) start = startOfToday();
      }
      // When both bounds collapse back to today the window is empty — clear it.
      const collapsed =
        isSameCalendarDay(start, startOfToday()) && isSameCalendarDay(end, endOfToday());
      column?.setFilterValue(collapsed ? undefined : { startDate: start, endDate: end });
      return;
    }
    if ("dateDirection" in preset) {
      column?.setFilterValue(active ? undefined : dateDirectionRange(preset.dateDirection));
      return;
    }
    column?.setFilterValue(active ? undefined : preset.values);
  };

  return (
    <>
      {presets.map((preset) => {
        const active = isPresetActive(table, preset);
        if (preset.variant === "button") {
          return (
            <Button
              key={preset.label}
              variant="outline"
              size="sm"
              className={cn(
                "h-8 shrink-0 border-transparent px-2.5 text-gray-600 shadow-none",
                active
                  ? "border-border bg-accent text-accent-foreground hover:bg-accent/80"
                  : "hover:border-border hover:bg-transparent hover:text-gray-900",
              )}
              onClick={() => toggle(preset, active)}
            >
              {preset.label}
            </Button>
          );
        }
        return (
          <button
            key={preset.label}
            onClick={() => toggle(preset, active)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-all",
              preset.className ?? "bg-gray-100 text-gray-700",
              active
                ? "ring-1 ring-gray-400/70 ring-offset-1"
                : "opacity-60 hover:opacity-100",
            )}
          >
            {preset.label}
          </button>
        );
      })}
    </>
  );
}

// ============================================================================
// Single Filters button: quick-filter presets + drill-in field filters
// ============================================================================

type FilterMenuSharedProps = {
  table: Table<any>;
  fields: FilterField[];
  quickFilters?: QuickFilterPreset[];
  /** Overrides the default "Clear all" behavior (e.g. to also clear a separate search). */
  onClearAll?: () => void;
  /** Count of active filters tracked outside these fields (e.g. free-text search),
   *  so "Clear all" stays visible when only that external state is active. */
  extraActiveCount?: number;
};

function getActiveFilterFields(
  table: Table<any>,
  fields: FilterField[],
  quickFilters: QuickFilterPreset[],
) {
  return fields.filter((field) => {
    if (!isFieldActive(table, field)) return false;
    const coveredByPreset = quickFilters.some(
      (preset) => presetFieldId(preset) === field.id && isPresetActive(table, preset),
    );
    return !coveredByPreset;
  });
}

export function filterActiveBarHasContent(
  table: Table<any>,
  fields: FilterField[],
  quickFilters: QuickFilterPreset[] = [],
  _extraActiveCount = 0,
): boolean {
  return getActiveFilterFields(table, fields, quickFilters).length > 0;
}

/** Popover trigger — stays pinned on the primary toolbar row next to search. */
export function FilterMenuButton({
  table,
  fields,
}: Pick<FilterMenuSharedProps, "table" | "fields">) {
  const [open, setOpen] = React.useState(false);
  const [drillFieldId, setDrillFieldId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) setDrillFieldId(null);
  }, [open]);

  const drillField = fields.find((f) => f.id === drillFieldId) ?? null;
  const totalCount = totalActiveCount(table, fields);
  // When the toolbar collapses the label to an icon, restore a hover tooltip so
  // the button still names itself; keep it tooltip-free while the label shows.
  const isCompact = useToolbarCompact();

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className={cn(
          "relative h-8 shrink-0 gap-1.5 overflow-visible text-gray-700",
          totalCount > 0 && "bg-accent text-accent-foreground",
        )}
        aria-label="Filters"
      >
        <ListFilter
          className={cn(
            "h-4 w-4",
            totalCount > 0 ? "text-accent-foreground" : "text-gray-500",
          )}
        />
        <span className={toolbarFilterLabelClass}>Filters</span>
        <FilterCountBadge count={totalCount} />
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {isCompact ? (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs font-normal">
              Filters
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <PopoverContent
        align="start"
        className={
          drillField && drillField.type === "date"
            ? "w-auto p-0"
            : "w-[260px] p-0"
        }
      >
        {drillField ? (
          <div>
            <button
              className="flex w-full items-center gap-1 border-b border-gray-100 px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setDrillFieldId(null)}
            >
              <ChevronLeft className="h-4 w-4 text-gray-400" />
              {drillField.label}
            </button>
            <FieldEditorContent table={table} field={drillField} />
          </div>
        ) : (
          <div>
            <Command>
              <CommandList>
                <CommandGroup heading="Filter by">
                  {fields.map((field) => {
                    const count = fieldCount(table, field);
                    return (
                      <CommandItem
                        key={field.id}
                        onSelect={() => setDrillFieldId(field.id)}
                        className="flex items-center"
                      >
                        <span>{field.label}</span>
                        <span className="ml-auto flex items-center gap-1 text-gray-400">
                          {count > 0 && (
                            <span className="rounded bg-gray-200 px-1.5 text-xs font-medium text-gray-700">
                              {count}
                            </span>
                          )}
                          <ChevronRight className="h-4 w-4" />
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Clears every field filter, then runs `onClearAll` (search, source folders, …). */
export function FilterClearAllButton({
  table,
  fields,
  onClearAll,
  extraActiveCount = 0,
}: Pick<FilterMenuSharedProps, "table" | "fields" | "onClearAll" | "extraActiveCount">) {
  const totalCount = totalActiveCount(table, fields);
  if (totalCount + extraActiveCount === 0) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 shrink-0 px-2 text-gray-500"
      onClick={() => {
        fields.forEach((field) => {
          if (field.type === "choice" && field.value !== field.defaultValue) {
            field.onChange(field.defaultValue);
          } else if (field.type === "custom" && field.active) {
            field.onClear();
          }
        });
        if (onClearAll) onClearAll();
        else clearAll(table, fields);
      }}
    >
      Clear all
      <X className="ml-1 h-3.5 w-3.5" />
    </Button>
  );
}

/** Active filter chips — wraps on its own row below the primary toolbar. */
export function FilterMenuActiveBar({
  table,
  fields,
  quickFilters = [],
}: FilterMenuSharedProps) {
  const activeFields = getActiveFilterFields(table, fields, quickFilters);

  if (activeFields.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      {activeFields.map((field) => (
        <FilterChip key={field.id} table={table} field={field} />
      ))}
    </div>
  );
}
